import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import articles from '@/data/articles';

const ArticlePage = () => {
  const { slug } = useParams();
  const article = articles.find(a => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      document.title = `${article.title} | The Second Seat`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000' }}>
        <div className="text-center" style={{ color: '#f0ebe0' }}>
          <p className="text-xl mb-4" style={{ fontFamily: 'EB Garamond, serif' }}>Article not found</p>
          <Link to="/writing" className="font-mono text-xs tracking-[0.15em] uppercase opacity-50 hover:opacity-100 transition-opacity border-b border-[#f0ebe0]/20 pb-0.5" style={{ color: '#f0ebe0' }}>
            Back to Writing
          </Link>
        </div>
      </div>
    );
  }

  const paragraphs = article.body.split('\n\n').filter(p => p.trim());

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#0a0a0a' }}>
      <nav className="sticky top-0 z-50 py-3 px-5 sm:px-8 flex items-center justify-between" style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(240,235,224,0.04)' }}>
        <Link to="/" className="font-serif text-sm sm:text-base italic opacity-60 hover:opacity-100 transition-opacity" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
          The Second Seat
        </Link>
        <Link to="/writing" className="font-mono text-[11px] sm:text-xs tracking-[0.15em] uppercase opacity-65 hover:opacity-100 transition-opacity" style={{ color: '#f0ebe0' }}>
          All Film Analysis
        </Link>
      </nav>

      <article className="max-w-2xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-12 sm:mb-16">
            <div className="font-mono text-[11px] sm:text-xs tracking-[0.15em] uppercase opacity-65 mb-4" style={{ color: '#f0ebe0' }}>
              {article.publication} &middot; {article.date}
            </div>
            <h1 className="text-3xl sm:text-3xl md:text-4xl leading-tight mb-4" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
              {article.title}
            </h1>
            <p className="text-base sm:text-lg italic opacity-75" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
              {article.desc}
            </p>
            {article.image && (
              <div className="mt-8 overflow-hidden rounded-lg" style={{ border: '1px solid rgba(240,235,224,0.06)' }}>
                <img src={article.image} alt={article.title} className="w-full object-cover grayscale sepia brightness-[0.7] contrast-[1.1]" />
              </div>
            )}
          </div>

          <div className="space-y-6 sm:space-y-8">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-lg sm:text-xl md:text-[1.35rem] leading-[1.85] opacity-85" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
                {p}
              </p>
            ))}
          </div>

          <div className="mt-16 pt-8" style={{ borderTop: '1px solid rgba(240,235,224,0.08)' }}>
            {article.sharedBySubject && (
              <div className="mb-8 pb-6" style={{ borderBottom: '1px solid rgba(232,196,122,0.18)' }}>
                <div className="font-mono text-[10px] sm:text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: '#e8c47a', opacity: 0.85 }}>
                  From the Other Seat
                </div>
                <p className="text-sm sm:text-base italic leading-relaxed opacity-80" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
                  {article.sharedBySubject.note}
                </p>
              </div>
            )}
            {article.relatedEpisode && (
              <div className="mb-8 pb-6" style={{ borderBottom: '1px solid rgba(240,235,224,0.08)' }}>
                <div className="font-mono text-[10px] sm:text-[10px] tracking-[0.2em] uppercase opacity-65 mb-2" style={{ color: '#f0ebe0' }}>
                  Companion episode
                </div>
                <Link to={article.relatedEpisode.slug} className="text-base sm:text-lg italic opacity-90 hover:opacity-100 transition-opacity inline-flex items-center gap-2" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
                  Episode {article.relatedEpisode.number} &middot; {article.relatedEpisode.label} <span aria-hidden="true">&rarr;</span>
                </Link>
              </div>
            )}
            <p className="text-sm italic opacity-60 mb-6" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
              Originally published in {article.publication}, {article.date}
            </p>
            <div className="flex gap-6">
              <Link to="/writing" className="font-mono text-[11px] sm:text-xs tracking-[0.12em] uppercase opacity-65 hover:opacity-100 transition-opacity" style={{ color: '#f0ebe0' }}>
                All Film Analysis
              </Link>
              <a href={article.originalUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] sm:text-xs tracking-[0.12em] uppercase opacity-65 hover:opacity-100 transition-opacity" style={{ color: '#f0ebe0' }}>
                Original Source
              </a>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default ArticlePage;
